import { urlApi } from '@/lib/urlApi';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { estRaisonDeconnexion } from './messages-auth';
import { useUserStore } from '@/stores/userStore';

/**
 * Client HTTP Axios — interceptors auth complets Phase 4.I.1.
 *
 * baseURL '/api/v1' → proxy Vite vers backend :3000
 * withCredentials: true → cookies refresh HttpOnly (Phase 4.A)
 *
 * Format réponse standard backend : { data: T, meta: {...} }
 * Format erreur backend           : { error: { code, message, details? } }
 */
/**
 * ⚠ AUCUN `Content-Type` PAR DEFAUT — ET C EST UN CORRECTIF, PAS UN OUBLI.
 *   L en-tete etait pose GLOBALEMENT, donc aussi sur les requetes SANS CORPS.
 *   Fastify refuse cette combinaison : `FST_ERR_CTP_EMPTY_JSON_BODY`, « Body
 *   cannot be empty when content-type is set to 'application/json' ». Mesure le
 *   18/08/2026 contre l API qui tourne : **les TREIZE `apiClient.delete()` de
 *   l ecran repondaient 400** — desactivation d un compte, purges de
 *   referentiels, suppression d une requete du Lab, retrait d une photo,
 *   suppression d un cluster, d une regle de segmentation. Aucun ne marchait.
 * ⚠ LA CAUSE AVAIT DEJA ETE TROUVEE UNE FOIS, ET CONTOURNEE AU LIEU D ETRE
 *   CORRIGEE : `modules/admin/import/lib/api.ts` documente que ce meme en-tete
 *   cassait le multipart, et repasse par `fetch` natif pour l eviter. Un
 *   contournement local laisse le defaut en place pour tous les autres appels.
 * ⚠ RIEN NE SE PERD EN LE RETIRANT — mesure, pas suppose : axios pose
 *   `application/json` DE LUI-MEME des qu un corps objet est fourni
 *   (POST /users -> 201, PATCH /users/:id -> 200, en-tete effectivement envoye)
 *   et n en pose AUCUN quand il n y a pas de corps (DELETE -> 204). Les appels
 *   qui exigent un autre type le declarent deja explicitement par requete
 *   (`multipart/form-data` de flashinfo), et un en-tete par requete l emporte.
 */
export const apiClient = axios.create({
  baseURL: urlApi('/api/v1'), // lot 3 §5 — VITE_API_URL au build, relatif en dev
  timeout: 30_000,
  withCredentials: true, // cookies refresh HttpOnly
});

// ─── Request interceptor — auto-inject JWT Bearer ────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    const token = useUserStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Le rafraîchissement du jeton — UN SEUL pour toute l'application ─────
/**
 * ⚠⚠ `#1090` — **LE SERVEUR N'ACCEPTE QUE LE DERNIER JETON D'UNE SESSION.** *Chaque
 *    rafraîchissement invalide le précédent. Un appel parti avec l'ancien jeton reçoit
 *    401 : il doit attendre LE rafraîchissement en cours, jamais en lancer un second —
 *    le serveur lirait un second emploi du même jeton de rafraîchissement.*
 *
 * ⚠ Une seule promesse, partagée par l'intercepteur Axios ET par `fetchAuthentifie` :
 *   tous les appels en 401 au même moment attendent le même rafraîchissement. En cas
 *   d'échec, la session est close et l'écran de connexion dit pourquoi (`M2`).
 */
let rafraichissementEnCours: Promise<string> | null = null;

export function rafraichirJeton(): Promise<string> {
  rafraichissementEnCours ??= (async () => {
    try {
      const { data } = await axios.post<{ access_token: string }>(
        urlApi('/api/v1/auth/refresh'),
        {},
        { withCredentials: true }
      );
      useUserStore.getState().setAccessToken(data.access_token);
      return data.access_token;
    } catch (refreshErr) {
        /**
         * `M2` (#552) — UN REFUS QUI N'EXPLIQUE PAS SE LIT COMME UNE PANNE.
         *
         * ⚠ Jusqu'ici toute perte de session renvoyait vers `/login` **sans un
         *   mot** : impossible de distinguer « votre session a atteint sa durée
         *   maximale » d'une déconnexion inexpliquée. Le serveur rend désormais
         *   un code à part (`SESSION_MAX_LIFETIME`) ; on le fait suivre pour que
         *   l'écran de connexion puisse le dire.
         * ⚠ RIEN DE SENSIBLE DANS L'URL — un code de raison, pas une donnée.
         */
        const codeRefus = (refreshErr as AxiosError<{ error?: { code?: string } }>)
          ?.response?.data?.error?.code;
        useUserStore.getState().logout();
        // `M2` (#553) — deux refus prevus, deux raisons distinctes. Un vol
        //   detecte ne doit pas se lire comme une fin de session ordinaire.
        // ⚠ TROISIEME RAISON AJOUTEE AU `#574` — L INACTIVITE ETAIT MUETTE.
        //   `SESSION_EXPIRED` tombait dans le `: ''` : la garde la plus
        //   FREQUENTE des trois (22 declenchements en 24 h au trail) etait la
        //   SEULE a ne rien dire. Hkm se retrouvait sur l ecran de connexion
        //   sans un mot, incapable de distinguer une garde d une panne — ce que
        //   le `#552` avait justement corrige pour les deux autres.
        // ⚠⚠ `#840` — **L'URL PORTE LE CODE, PLUS UN MOT FRANÇAIS.** *`plafond`,
        //   `jeton-rejoue`, `inactivite` étaient une traduction intermédiaire :
        //   trois slugs à maintenir en face de trois codes. `LoginPage` lit
        //   désormais le code lui-même et le rend par le catalogue — une source.
        // ⚠ *La règle du `#552` tient : un code de raison, RIEN de sensible.*
        const raison = estRaisonDeconnexion(codeRefus ?? null)
          ? `?raison=${codeRefus}`
          : '';
        window.location.href = `/login${raison}`;
        throw refreshErr;
    } finally {
      rafraichissementEnCours = null;
    }
  })();
  return rafraichissementEnCours;
}

// ─── Response interceptor — 401 → refresh → retry ────────────────────────
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const is401 = error.response?.status === 401;
    const isRetry = original._retry;
    const isAuthRoute =
      original.url?.includes('/auth/refresh') ||
      original.url?.includes('/auth/login') ||
      original.url?.includes('/auth/2fa-validate');

    if (is401 && !isRetry && !isAuthRoute) {
      original._retry = true;
      const newToken = await rafraichirJeton();
      original.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(original);
    }

    // ⚠ `#1044` — un corps d'erreur en Blob (téléchargement) redevient lisible ici,
    //   sinon TOUT refus ressortait en `UNKNOWN_ERROR` : voir `corpsErreurLisible`.
    if (error.response) {
      error.response.data = await corpsErreurLisible(error.response.data);
    }

    // Format erreur backend standardisé
    const backendError = error.response?.data as
      | { error?: { code?: string; message?: string; details?: Array<{ field: string; issue: string }> } }
      | undefined;

    return Promise.reject({
      status: error.response?.status ?? 0,
      code: backendError?.error?.code ?? 'UNKNOWN_ERROR',
      message: backendError?.error?.message ?? error.message,
      details: backendError?.error?.details ?? [],
      raw: error,
    });
  }
);

/**
 * ⚠⚠ `#1090` — **« PREMIER ESSAI ÉCHOUE, SECOND PASSE ».** *Six écrans (envois
 *    d'import, référentiels en masse, validation, trois exports) appelaient `fetch`
 *    à nu, avec le jeton du store : ils ne connaissaient pas le rafraîchissement.
 *    Jeton invalidé entre-temps → 401 à l'écran ; un autre appel rafraîchissait → le
 *    second essai passait.*
 *
 * ⚠ Ils passent désormais ici : le jeton courant est posé, et un 401 attend LE
 *   rafraîchissement partagé (`rafraichirJeton`) puis rejoue la requête UNE fois.
 *   Chaque appelant garde sa lecture de la réponse (blob, `Content-Disposition`,
 *   corps d'erreur) — et aucun délai d'Axios ne coupe un gros envoi.
 */
export async function fetchAuthentifie(url: string, init: RequestInit = {}): Promise<Response> {
  const envoyer = (jeton: string | null) => {
    const headers = new Headers(init.headers);
    if (jeton) headers.set('Authorization', `Bearer ${jeton}`);
    return fetch(url, { ...init, headers, credentials: 'include' });
  };
  const res = await envoyer(useUserStore.getState().accessToken);
  if (res.status !== 401) return res;
  return envoyer(await rafraichirJeton());
}

/**
 * ⚠⚠ `#1044` — **UN TÉLÉCHARGEMENT PERDAIT LE MOT DU SERVEUR.** *Une requête en
 *    `responseType: 'blob'` (l'export Excel du Labo) reçoit son corps d'erreur en **Blob** :
 *    `data.error.code` vaut alors `undefined`, et TOUT refus — 400, 403, 413, 500 — ressortait
 *    en `UNKNOWN_ERROR`, un code qui n'existe dans aucun catalogue et que l'écran affichait
 *    **tel quel**. Hkm l'a vu le 16/09 sur un export de 19 545 lignes.*
 *
 * ⚠ *Le corps est relu en TEXTE puis analysé. Si ce n'est pas du JSON, **on n'invente rien** :
 *   la valeur d'origine est rendue, et le repli `UNKNOWN_ERROR` s'applique comme avant.*
 */
export async function corpsErreurLisible(donnees: unknown): Promise<unknown> {
  if (!(donnees instanceof Blob)) return donnees;
  try {
    return JSON.parse(await lireBlobEnTexte(donnees)) as unknown;
  } catch {
    return donnees;
  }
}

/**
 * ⚠ **DEUX CHEMINS, ET LE SECOND N'EST PAS DÉCORATIF** : *mesuré au banc — dans jsdom,
 *   `Blob.prototype.text` **n'existe pas** (`b.text is not a function`). Sans ce repli
 *   `FileReader`, le témoin ne pourrait jamais exécuter la lecture, et le correctif ne
 *   serait vérifié nulle part. Le navigateur, lui, prend la première branche.*
 */
async function lireBlobEnTexte(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise<string>((resoudre, rejeter) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resoudre(String(lecteur.result ?? ''));
    lecteur.onerror = () => rejeter(lecteur.error ?? new Error('lecture du corps impossible'));
    lecteur.readAsText(blob);
  });
}

export type ApiError = {
  status: number;
  code: string;
  message: string;
  details: Array<{ field: string; issue: string }>;
  raw: unknown;
};
